// External Imports
import { UtilityContext } from "../../context/util-context";
import { Fragment, useContext, useEffect, useState } from "react";
import {
  MdCheck,
  MdClose,
  MdDelete,
  MdHelp,
  MdPayments,
  MdRefresh,
} from "react-icons/md";
import { useSearchParams } from "react-router-dom";

// Internal Imports
import { requestPartnerAPI } from "../../api/server";
import { constructDateObject, isoDateToString } from "../../util/format-date";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import VehiclesRevenue from "./VehiclesRevenue";
import { FaEdit } from "react-icons/fa";
import { formatCurrency } from "../../util/format-text";
import uploadFile from "../../util/uploadFile";
import Modal from "../UI/Modal";
import { Transition } from "@headlessui/react";

/** ========================================================================
 * * Vehicles Information
 * Renders a page displaying vehicle information.
 * ========================================================================== */

export default function VehicleInfo() {
  // Context
  const { navigate, setIssue } = useContext(UtilityContext);

  // Get VIN from URL
  const [searchParams] = useSearchParams();
  const vin = searchParams.get("vin");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [item, setItem] = useState();
  const [edit, setEdit] = useState(false);
  const [vehicle, setVehicle] = useState();
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(false);

  // ========================================== FETCH VEHICLE
  const fetchVehicleHandler = async () => {
    try {
      setLoading(true);
      const data = await requestPartnerAPI("fetch-vehicle", { vin: vin });
      setVehicle(data);
      setVehicles([data]);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };
  // ========================================== DELETE VEHICLE
  const deleteVehicleHandler = async () => {
    try {
      setLoading(true);
      const data = await requestPartnerAPI("delete-application", { vin: vin });
      navigate("/vehicles");
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };

  // ========================================== UPDATE VEHICLE
  const updateVehicleHandler = async () => {
    try {
      const payload = {
        vin: vin,
        license: document.getElementById("license").value,
        licenseState: document.getElementById("licenseState").value,
        institution: document.getElementById("institution").value,
        financeAmount: document.getElementById("financeAmount").value,
        loanTerm: document.getElementById("loanTerm").value,
        monthlyPayment: document.getElementById("monthlyPayment").value,
      };
      const due = document.getElementById("dueDate").value;
      if (due)
        payload.dueDate = constructDateObject(due, "13:00:00").toISOString();
      const fileFields = [
        "registeration",
        "loanDocument",
        "title",
        "billOfSale",
        "vehicleInspectionReport",
        "insurance",
      ];
      setLoading(true);
      for (const field of fileFields) {
        const fileInput = document.getElementById(field);
        if (fileInput?.files[0]) {
          const path = `partners/${
            vehicle?.partnerId
          }/${vin}/${field}-${new Date().getTime()}`;
          setItem(field);
          const url = await uploadFile(path, fileInput.files[0], setUploading);
          payload[field] = url;
        }
      }
      await requestPartnerAPI("edit-application", payload);
      setUploading(false);
      setEdit(false);
      fetchVehicleHandler();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };

  useEffect(() => {
    fetchVehicleHandler();
  }, []);

  return (
    <div className="grid gap-6 bg">
      <Heading
        isBackArrow={true}
        title="Vehicle Information"
        description="Once the vehicle is onboarded the information below can only be updated by the POSH Team. If you see any issues please reach out to us."
      >
        <div
          className="flex items-center text-sm lg:space-x-2 btn-secondary"
          onClick={() => navigate(`/payments?query=${vin}`)}
        >
          <p className="hidden lg:block">Payment History</p>
          <MdPayments className="text-xl text-stone-200" />
        </div>
        <div
          className="flex items-center text-sm lg:space-x-2 btn-secondary"
          onClick={fetchVehicleHandler}
        >
          <p className="hidden lg:block">Refresh</p>
          <MdRefresh className="text-xl text-stone-200" />
        </div>
        {!loading && !vehicle?.onboarded && (
          <div
            className="flex items-center text-sm lg:space-x-2 btn-warning"
            onClick={deleteVehicleHandler}
          >
            <p className="hidden lg:block">Delete</p>
            <MdDelete className="text-xl text-stone-200" />
          </div>
        )}
      </Heading>

      <div className="grid gap-12 lg:grid-cols-3">
        {loading ? (
          <div className="w-full rounded-md animate-pulse bg-stone-600 h-80"></div>
        ) : (
          <div className="grid gap-4">
            <div className="flex items-center space-x-4">
              {vehicle?.make?.image && (
                <img
                  src={vehicle?.vehicle?.make?.image}
                  className="object-contain w-20"
                />
              )}

              <div>
                <p className="text-2xl font-bold">
                  {vehicle?.year} {vehicle?.make} {vehicle?.model}
                </p>
                <p className="text-base text-stone-400">
                  {vehicle?.vehicle?.trim} {vehicle?.vin}
                </p>
              </div>
            </div>

            {vehicle?.vehicle?.cornerImage && (
              <div
                onClick={() => setStats(!stats)}
                className="relative flex items-center justify-center cursor-pointer"
              >
                <div className="absolute m-auto text-xs">
                  <Transition
                    show={stats}
                    enter="transition-opacity duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="p-4 rounded-lg bg-stone-700">
                      <div className="mb-4">
                        <div className="flex items-center justify-between">
                          <p className="mb-1 text-sm font-medium">
                            Vehicle Status
                          </p>
                          <MdClose
                            className="w-5 h-5 cursor-pointer"
                            onClick={() => setStats(false)}
                          />
                        </div>
                        <hr className="hr" />
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-2">
                        <DetailPair
                          label="Status"
                          classes={
                            vehicle?.vehicle?.activeBookingId
                              ? "text-green-600"
                              : vehicle?.status === "maintenance"
                                ? "text-yellow-600"
                                : "text-stone-200"
                          }
                          value={
                            vehicle?.vehicle?.activeBookingId
                              ? "Booked"
                              : vehicle?.status === "maintenance"
                                ? "Maintenance"
                                : "Available"
                          }
                          loading={loading}
                        />
                        <DetailPair
                          label="Mileage"
                          value={
                            vehicle?.vehicle?.mileage
                              ? `${vehicle?.vehicle?.mileage}mi`
                              : undefined
                          }
                          loading={loading}
                        />
                      </div>
                      <DetailPair
                        label="Location"
                        value={
                          vehicle?.vehicle?.currentLocation ||
                          "Location Dedacted -  Please contact POSH Team."
                        }
                        loading={loading}
                      />
                    </div>
                  </Transition>
                </div>

                <img
                  src={vehicle?.vehicle?.cornerImage}
                  className="object-contain"
                />
              </div>
            )}
            {!vehicle?.agreement && vehicle?.onboarded && (
              <div
                className="text-sm btn-warning animate-pulse"
                onClick={(e) =>
                  navigate(`/vehicles/agreement?vin=${vehicle?.vin}`, e)
                }
              >
                Sign Vehicle Agreement
              </div>
            )}
            {!vehicle?.powerOfAttorney && (
              <div
                className="text-sm btn-warning animate-pulse"
                onClick={(e) =>
                  navigate(`/vehicles/poa?vin=${vehicle?.vin}`, e)
                }
              >
                Sign Power Of Attorney
              </div>
            )}
          </div>
        )}
        {loading ||
          (vehicle?.onboarded && (
            <div className="lg:col-span-2">
              <VehiclesRevenue vehicles={vehicles} individual={true} />
            </div>
          ))}
      </div>

      <Heading
        title="Vehicle Details"
        description="Once the vehicle is onboarded the information below can only be updated by the POSH Team. If you see any issues please reach out to us by clicking the report issue button on the right."
      >
        {edit ? (
          <Fragment>
            <MdCheck className="icon" onClick={updateVehicleHandler} />
            <MdClose className="icon" onClick={() => setEdit(false)} />
          </Fragment>
        ) : (
          <FaEdit className="icon" onClick={() => setEdit(true)} />
        )}
        <div
          onClick={() => setIssue(true)}
          className="flex items-center space-x-2 text-sm btn-secondary"
        >
          <p>Report Issue</p>
          <MdHelp className="text-xl text-stone-200" />
        </div>
      </Heading>
      <div className="lg:grid-cols-6 info">
        <DetailPair
          label="Date Added"
          value={isoDateToString(vehicle?.dateCreated)}
          loading={loading}
        />
        <DetailPair
          label="License"
          value={vehicle?.license}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "license",
            type: "text",
            defaultValue: vehicle?.license,
          }}
        />
        <DetailPair
          label="State"
          value={vehicle?.licenseState}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "licenseState",
            type: "text",
            maxLength: 2,
            defaultValue: vehicle?.licenseState,
          }}
        />
        <LinkPair
          label="Registration"
          link={vehicle?.registeration}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "registeration",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Loan Documents"
          link={vehicle?.loanDocument}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "loanDocument",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <DetailPair
          loading={loading}
          label="Status"
          value={vehicle?.onboarded ? "ONBOARDED" : "NOT ONBOARDED"}
          classes={
            vehicle?.onboarded
              ? "font-semibold text-green-600 text-lg"
              : "font-semibold text-red-600 text-lg"
          }
        />
        <DetailPair
          label="Institution"
          value={vehicle?.institution}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "institution",
            type: "text",
            defaultValue: vehicle?.institution,
          }}
        />
        <DetailPair
          label="Amount Financed"
          value={formatCurrency(vehicle?.financeAmount)}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "financeAmount",
            type: "number",
            step: "0.01",
            defaultValue: vehicle?.financeAmount,
          }}
        />
        <DetailPair
          label="Loan Term"
          value={vehicle?.loanTerm}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "loanTerm",
            type: "number",
            defaultValue: vehicle?.loanTerm,
          }}
        />
        <DetailPair
          label="Monthly Payment"
          classes="font-semibold text-lg"
          value={formatCurrency(vehicle?.monthlyPayment)}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "monthlyPayment",
            type: "number",
            step: "0.01",
            defaultValue: vehicle?.monthlyPayment,
          }}
        />
        <DetailPair
          label="Next Due Date"
          value={isoDateToString(vehicle?.dueDate, "do MMMM")}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "dueDate",
            type: "date",
            defaultValue: isoDateToString(vehicle?.dueDate, "yyyy-MM-dd"),
          }}
        />
        <DetailPair
          label="Last Payment"
          value={isoDateToString(vehicle?.lastPayment, "do MMMM")}
          loading={loading}
        />
        <LinkPair
          label="Title"
          link={vehicle?.title}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "title",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Bill of Sale"
          link={vehicle?.billOfSale}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "billOfSale",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Inspection Report"
          link={vehicle?.vehicleInspectionReport}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "vehicleInspectionReport",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        <LinkPair
          label="Insurance"
          link={vehicle?.insurance}
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "insurance",
            type: "file",
            accept: "image/*,application/pdf",
          }}
        />
        {vehicle?.powerOfAttorney && (
          <LinkPair
            label="Power of Attorney"
            link={vehicle?.powerOfAttorney}
            loading={loading}
          />
        )}
        {vehicle?.agreement && (
          <LinkPair
            label="Vehicle Agreement"
            link={vehicle.agreement}
            loading={loading}
          />
        )}
      </div>
      {uploading && (
        <p className="text-sm text-stone-400">
          Uploading {item}... {uploading}%
        </p>
      )}
    </div>
  );
}
